import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import SEO from "../../components/SEO/SEO";
import { getDemosByVertical } from "../../api/demoTenants";
import { rememberVerticalIntent } from "../../utils/verticalIntent";
import { getVerticalDetailConfig } from "./verticalDetailData";
import PricingSection from "../../components/ai-chatbot-components/PricingSection/PricingSection";

// Background images
import cardBg from "../../images/bg/pricing-bg01.png";
import serviceBg from "../../images/bg/pricing-bg.png";

// Sub icons for titles
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

// Icons for service features
import icon01 from "../../images/icon/service-icon01.svg";
import icon02 from "../../images/icon/service-icon02.svg";
import icon03 from "../../images/icon/service-icon03.svg";
import icon04 from "../../images/icon/service-icon04.svg";
import icon05 from "../../images/icon/service-icon05.svg";
import icon06 from "../../images/icon/service-icon06.svg";
import icon07 from "../../images/icon/service-icon07.svg";

// Vertical configuration - slug to i18n key mapping and metadata
const VERTICAL_CONFIG: Record<string, {
  i18nKey: string;
  icon: string;
  hasStorefront: boolean;
  hasCustomerPortal: boolean;
  badge?: string;
  heroIcon: string;
}> = {
  hotel: { i18nKey: "hotel", icon: icon01, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-hotel" },
  restaurant: { i18nKey: "restaurant", icon: icon02, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-utensils" },
  travel: { i18nKey: "travel", icon: icon06, hasStorefront: true, hasCustomerPortal: true, badge: "VOYAGE AI", heroIcon: "fa-plane" },
  healthcare: { i18nKey: "healthcare", icon: icon04, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-hospital" },
  education: { i18nKey: "education", icon: icon03, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-graduation-cap" },
  logistics: { i18nKey: "logistics", icon: icon05, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-truck" },
  "real-estate": { i18nKey: "realEstate", icon: icon07, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-home" },
  retail: { i18nKey: "retail", icon: icon01, hasStorefront: true, hasCustomerPortal: false, heroIcon: "fa-shopping-cart" },
  manufacturing: { i18nKey: "manufacturing", icon: icon05, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-industry" },
  construction: { i18nKey: "construction", icon: icon03, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-hard-hat" },
  consulting: { i18nKey: "consulting", icon: icon07, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-briefcase" },
  "it-services": { i18nKey: "itServices", icon: icon05, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-laptop-code" },
  ecommerce: { i18nKey: "ecommerce", icon: icon06, hasStorefront: true, hasCustomerPortal: true, heroIcon: "fa-shopping-bag" },
  "car-dealer": { i18nKey: "carDealer", icon: icon02, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-car" },
  "bike-dealer": { i18nKey: "bikeDealer", icon: icon03, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-motorcycle" },
  "salon-spa": { i18nKey: "salonSpa", icon: icon04, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-spa" },
  "gym-fitness": { i18nKey: "gym", icon: icon05, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-dumbbell" },
  "arena-gaming": { i18nKey: "arena", icon: icon06, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-gamepad" },
  "study-abroad": { i18nKey: "studyAbroad", icon: icon03, hasStorefront: true, hasCustomerPortal: true, badge: "New", heroIcon: "fa-globe-americas" },
};

// Verticals that have one or more dedicated competitor comparison pages at /compare/:slug.
const COMPARISON_LINKS: Record<string, Array<{ slug: string; name: string }>> = {
  hotel: [
    { slug: "rentok", name: "RentOk" },
    { slug: "ezee", name: "eZee" },
    { slug: "hotelogix", name: "Hotelogix" },
    { slug: "cloudbeds", name: "Cloudbeds" },
  ],
  restaurant: [{ slug: "petpooja", name: "Petpooja" }],
  "salon-spa": [{ slug: "zenoti", name: "Zenoti" }],
  "gym-fitness": [{ slug: "mindbody", name: "Mindbody" }],
  "real-estate": [{ slug: "selldo", name: "Sell.Do" }],
  healthcare: [{ slug: "practo", name: "Practo" }],
  education: [{ slug: "teachmint", name: "Teachmint" }],
};

// Verticals that have a dedicated top-level solution page for a distinct buyer
const SOLUTION_CALLOUTS: Record<string, {
  path: string;
  icon: string;
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  demoVertical?: string;
  demoCta?: string;
}> = {
  hotel: {
    path: "/industry/pg-hostel-management-software",
    icon: "fa-bed",
    eyebrow: "Also in the Hotel vertical",
    title: "Running a PG, hostel or co-living block?",
    body: "That's a different product on the same platform — beds instead of rooms, monthly rent instead of nightly folios, tenants instead of guests. Sub-metered electricity, deposits with formal settlement, notice periods, and a tenant portal that collects rent online. It has its own page and its own live demo.",
    cta: "See PG & Hostel Management",
    demoVertical: "pg",
    demoCta: "Try the PG Demo",
  },
};

// Core modules available to all verticals
const CORE_MODULES = [
  { icon: "fa-calculator", nameKey: "industryDetail.coreModules.accounting" },
  { icon: "fa-users", nameKey: "industryDetail.coreModules.hr" },
  { icon: "fa-handshake", nameKey: "industryDetail.coreModules.crm" },
  { icon: "fa-qrcode", nameKey: "industryDetail.coreModules.upi" },
  { icon: "fa-chart-line", nameKey: "industryDetail.coreModules.analytics" },
  { icon: "fa-comments", nameKey: "industryDetail.coreModules.connect" },
  { icon: "fa-shield-alt", nameKey: "industryDetail.coreModules.compliance" },
  { icon: "fa-stamp", nameKey: "industryDetail.coreModules.esign" },
];

const pageStyles = `
  /* Unified Industry Hero Section */
  .pg-hero-section {
    position: relative;
    padding: 140px 0 70px;
    background: radial-gradient(circle at 50% 20%, rgba(0, 255, 151, 0.08) 0%, transparent 60%);
    overflow: hidden;
  }
  .pg-badge-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.35);
    color: var(--color-primary);
    padding: 7px 18px;
    border-radius: 30px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 22px;
    box-shadow: 0 4px 15px rgba(0, 255, 151, 0.15);
  }
  .pg-hero-title {
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 20px;
    color: #ffffff;
    letter-spacing: -0.5px;
  }
  @media (max-width: 991px) {
    .pg-hero-title { font-size: 2.3rem; }
  }
  @media (max-width: 575px) {
    .pg-hero-title { font-size: 1.85rem; }
  }
  .pg-hero-desc {
    font-size: 1.15rem;
    line-height: 1.75;
    opacity: 0.85;
    max-width: 760px;
    margin: 0 auto 32px;
  }
  .pg-hero-trust-bullets {
    display: flex;
    justify-content: center;
    gap: 24px;
    flex-wrap: wrap;
    font-size: 13px;
    opacity: 0.8;
    margin-top: 24px;
  }
  .pg-hero-trust-bullets span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* Hero Button Group */
  .pg-hero-btn-group {
    display: flex;
    gap: 14px;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
  .pg-cta-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-radius: 30px !important;
    padding: 15px 30px !important;
    text-decoration: none !important;
    transition: all 0.25s ease;
    cursor: pointer;
    box-sizing: border-box;
    white-space: nowrap;
  }
  .pg-cta-btn-primary {
    background: var(--color-primary) !important;
    color: #060e1a !important;
    border: 1px solid var(--color-primary) !important;
    box-shadow: 0 4px 18px rgba(0, 255, 151, 0.25);
  }
  .pg-cta-btn-primary:hover {
    background: #00e084 !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.4);
  }
  .pg-cta-btn-demo {
    background: transparent !important;
    color: #fff !important;
    border: 1px solid rgba(0, 255, 151, 0.4) !important;
  }
  .pg-cta-btn-demo:hover {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    transform: translateY(-2px);
  }
  .pg-cta-btn-plans {
    background: rgba(255, 255, 255, 0.06) !important;
    color: #fff !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
  }
  .pg-cta-btn-plans:hover {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    transform: translateY(-2px);
  }

  /* Authentic iBOS Dashboard Window Mockup */
  .pg-preview-window {
    background: #0b1120;
    border: 1px solid rgba(0, 255, 151, 0.3);
    border-radius: 16px;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.75), 0 0 35px rgba(0, 255, 151, 0.12);
    overflow: hidden;
    margin: 40px auto 0;
    max-width: 1080px;
    backdrop-filter: blur(16px);
  }
  .pg-preview-titlebar {
    background: #070d18;
    padding: 12px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .pg-preview-dots {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .pg-preview-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .pg-preview-urlbar {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 4px 14px;
    font-size: 11px;
    color: #94a3b8;
    font-family: monospace;
  }
  .pg-preview-workspace-badge {
    font-size: 11px;
    color: var(--color-primary);
    background: rgba(0, 255, 151, 0.08);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 3px 10px;
    font-weight: 600;
  }

  /* iBOS Header */
  .pg-ibos-header {
    padding: 18px 22px 14px;
    background: #0f172a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
  }
  .pg-ibos-header-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #f8fafc;
    margin: 0;
  }
  .pg-ibos-header-sub {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
  }
  .pg-ibos-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .pg-ibos-prop-select {
    background: #1e293b;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #f1f5f9;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
  }
  .pg-ibos-action-btn {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #f1f5f9;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: default;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .pg-ibos-action-btn-primary {
    background: var(--color-primary);
    color: #0b1226 !important;
    border: 1px solid var(--color-primary);
    font-weight: 700;
  }

  /* iBOS 8 KPI Stat Grid */
  .pg-ibos-stats-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 8px;
    padding: 14px 22px;
    background: #0b1222;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  @media (max-width: 1199px) {
    .pg-ibos-stats-grid { grid-template-columns: repeat(4, 1fr); }
  }
  @media (max-width: 575px) {
    .pg-ibos-stats-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .pg-ibos-stat-card {
    background: #131d35;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .pg-ibos-stat-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .pg-ibos-stat-val {
    font-size: 1.15rem;
    font-weight: 700;
    color: #f8fafc;
    margin-top: 4px;
  }
  .pg-ibos-stat-sub {
    font-size: 10px;
    opacity: 0.75;
    margin-top: 2px;
  }

  /* iBOS Tab Navigation */
  .pg-ibos-tabs-bar {
    display: flex;
    gap: 4px;
    padding: 10px 22px 0;
    background: #0e162b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    overflow-x: auto;
  }
  .pg-ibos-tab-btn {
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: #94a3b8;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  .pg-ibos-tab-btn:hover {
    color: #f1f5f9;
  }
  .pg-ibos-tab-btn.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
    background: rgba(0, 255, 151, 0.04);
  }
  .pg-ibos-tab-badge {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
  }
  .pg-ibos-tab-badge.amber { background: rgba(245, 158, 11, 0.2); color: #fbbf24; }
  .pg-ibos-tab-badge.green { background: rgba(0, 255, 151, 0.15); color: #00ff97; }

  /* Badges */
  .pg-ibos-badge-paid {
    background: rgba(0, 255, 151, 0.15);
    color: #00ff97;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }
  .pg-ibos-badge-due {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }
  .pg-ibos-badge-notice {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }

  /* Operational Trust Bar */
  .pg-trust-bar {
    padding: 30px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(10, 16, 32, 0.4);
  }
  .pg-trust-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 8px 12px;
  }
  .pg-trust-icon {
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 18px;
  }
  .pg-trust-title {
    font-size: 14px;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 3px;
  }
  .pg-trust-desc {
    font-size: 12px;
    opacity: 0.75;
    line-height: 1.5;
    margin: 0;
  }

  /* Step Cards */
  .pg-step-card {
    background: rgba(15, 23, 48, 0.65);
    border: 1px solid rgba(0, 255, 151, 0.16);
    border-radius: 16px;
    padding: 32px 26px;
    height: 100%;
    position: relative;
    backdrop-filter: blur(12px);
    transition: all 0.3s ease;
  }
  .pg-step-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-3px);
  }
  .pg-step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--color-primary);
    color: #060e1a;
    font-weight: 800;
    font-size: 16px;
    margin-bottom: 18px;
  }

  /* Pillars */
  .pg-pillar-card {
    background: rgba(15, 23, 48, 0.65);
    border: 1px solid rgba(0, 255, 151, 0.16);
    border-radius: 16px;
    padding: 32px;
    height: 100%;
    backdrop-filter: blur(12px);
    transition: all 0.3s ease;
  }
  .pg-pillar-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  .pg-pillar-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--color-primary);
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.3);
    padding: 3px 10px;
    border-radius: 12px;
    margin-bottom: 12px;
  }

  /* Comparison Table */
  .pg-compare-table {
    width: 100%;
    border-collapse: collapse;
    background: rgba(11, 18, 38, 0.7);
    border: 1px solid rgba(0, 255, 151, 0.2);
    border-radius: 16px;
    overflow: hidden;
  }
  .pg-compare-table th,
  .pg-compare-table td {
    padding: 16px 20px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 14px;
  }
  .pg-compare-table thead th {
    background: rgba(0, 255, 151, 0.08);
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Feature Grid Cards */
  .industry-detail-feature-card {
    background: rgba(15, 20, 40, 0.65);
    border: 1px solid rgba(0, 255, 151, 0.15);
    border-radius: 16px;
    padding: 24px;
    height: 100%;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .industry-detail-feature-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  .industry-detail-feature-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(0, 255, 151, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 20px;
    margin-bottom: 16px;
  }



  /* Module Grid */
  .industry-detail-module-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  @media (max-width: 991px) {
    .industry-detail-module-grid { grid-template-columns: repeat(3, 1fr); }
  }
  @media (max-width: 575px) {
    .industry-detail-module-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .industry-detail-module-item {
    background: rgba(15, 20, 40, 0.65);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .industry-detail-module-item:hover {
    border-color: var(--color-primary);
  }
  .industry-detail-module-item i {
    font-size: 24px;
    color: var(--color-primary);
    margin-bottom: 8px;
    display: block;
  }
  .industry-detail-module-item span {
    font-size: 12px;
    opacity: 0.8;
  }

  /* Restaurant Table Floor Plan */
  .ibos-table-tile {
    position: relative;
    border-radius: 12px;
    padding: 14px 10px;
    text-align: center;
    cursor: default;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 105px;
  }
  .ibos-table-available {
    background: rgba(16, 185, 129, 0.12);
    border: 2px solid #10b981;
    color: #fff;
  }
  .ibos-table-occupied {
    background: rgba(239, 68, 68, 0.14);
    border: 2px solid #ef4444;
    color: #fff;
  }
  .ibos-table-reserved {
    background: rgba(59, 130, 246, 0.14);
    border: 2px solid #3b82f6;
    color: #fff;
  }
  .ibos-table-billed {
    background: rgba(245, 158, 11, 0.14);
    border: 2px solid #f59e0b;
    color: #fff;
  }

  /* Restaurant KDS Cards */
  .ibos-kds-card {
    background: #121c33;
    border-radius: 12px;
    overflow: hidden;
    border: 3px solid #334155;
    text-align: left;
    height: 100%;
  }
  .ibos-kds-urgent { border-color: #ef4444; }
  .ibos-kds-warning { border-color: #f59e0b; }
  .ibos-kds-normal { border-color: #10b981; }

  /* Hotel Room Rack */
  .ibos-room-tile {
    background: #141e35;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 10px 8px;
    text-align: center;
    position: relative;
    transition: all 0.2s ease;
  }
  .ibos-room-tile:hover {
    border-color: var(--color-primary);
  }

  /* Turnstile & OPD Cards */
  .ibos-turnstile-row {
    background: #131d35;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
  }
  .ibos-opd-card {
    background: #131d35;
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 12px;
    padding: 18px 22px;
    text-align: left;
  }

  .industry-detail-capability-tag {
    display: inline-flex;
    align-items: center;
    background: rgba(0, 255, 151, 0.08);
    border: 1px solid rgba(0, 255, 151, 0.25);
    color: var(--color-primary);
    font-size: 14px;
    font-weight: 500;
    padding: 10px 20px;
    border-radius: 12px;
    margin: 6px;
    backdrop-filter: blur(8px);
  }

  .industry-detail-cta-section {
    background-image: url(${cardBg});
    border-radius: 20px;
  }
`;

const IndustryDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const config = slug ? VERTICAL_CONFIG[slug] : undefined;
  const comparisonLinks = slug ? COMPARISON_LINKS[slug] : undefined;
  const callout = slug ? SOLUTION_CALLOUTS[slug] : undefined;

  const [hasDemos, setHasDemos] = useState(false);
  const [hasCalloutDemo, setHasCalloutDemo] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("");
  const [activeFaqId, setActiveFaqId] = useState<number | null>(null);

  // Track intent
  useEffect(() => {
    if (config) rememberVerticalIntent(slug);
  }, [slug, config]);

  // Check demo availability
  useEffect(() => {
    if (!slug) return;
    getDemosByVertical(slug).then((data) => {
      setHasDemos(data !== null && data.demos.length > 0);
    });
  }, [slug]);

  const calloutDemoVertical = callout?.demoVertical;
  useEffect(() => {
    if (!calloutDemoVertical) return;
    getDemosByVertical(calloutDemoVertical).then((data) => {
      setHasCalloutDemo(data !== null && data.demos.length > 0);
    });
  }, [calloutDemoVertical]);

  const title = config ? t(`industriesData.${config.i18nKey}.title`, { defaultValue: slug || "Business" }) : (slug || "Business");
  const description = config ? t(`industriesData.${config.i18nKey}.desc`, { defaultValue: "" }) : "";
  const detailDesc = config ? t(`industryDetail.${config.i18nKey}.description`, { defaultValue: description }) : description;

  // Get rich domain details from our registry
  const configData = getVerticalDetailConfig(slug || "", title, detailDesc);

  // Set default active tab on load
  useEffect(() => {
    if (configData.tabs && configData.tabs.length > 0 && !activeTab) {
      setActiveTab(configData.tabs[0].id);
    }
  }, [configData.tabs, activeTab]);

  // Get features list from i18n
  const features = useMemo(() => {
    if (!config) return [];
    const featuresStr = t(`industryDetail.${config.i18nKey}.features`, { defaultValue: "" });
    if (!featuresStr) return [];
    return featuresStr.split("|").map((f: string) => f.trim()).filter(Boolean);
  }, [config, t]);

  // Aliases & canonical redirects
  const CANONICAL_PATH_REDIRECTS: Record<string, string> = {
    pg: "/industry/pg-hostel-management-software",
    "pg-hostel": "/industry/pg-hostel-management-software",
    hostel: "/industry/pg-hostel-management-software",
    "co-living": "/industry/pg-hostel-management-software",
    coliving: "/industry/pg-hostel-management-software",
    "pg-hostel-management-software": "/industry/pg-hostel-management-software",
  };
  if (slug && CANONICAL_PATH_REDIRECTS[slug]) {
    return <Navigate to={CANONICAL_PATH_REDIRECTS[slug]} replace />;
  }

  const LEGACY_SLUG_REDIRECTS: Record<string, string> = {
    trading: "retail",
    barter: "retail",
  };
  if (slug && LEGACY_SLUG_REDIRECTS[slug]) {
    return <Navigate to={`/industry/${LEGACY_SLUG_REDIRECTS[slug]}`} replace />;
  }

  if (!config) {
    return <Navigate to="/404" replace />;
  }

  const activeTabObj = configData.tabs.find((t) => t.id === activeTab) || configData.tabs[0];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${title} Software India`,
    serviceType: `${title} Management System`,
    provider: {
      "@type": "Organization",
      name: "AXOIX TECHNOLOGIES PRIVATE LIMITED",
      url: "https://axoix.com",
    },
    areaServed: { "@type": "Country", name: "India" },
    description: configData.subtitle,
    url: `https://axoix.com/industry/${slug}`,
    category: title,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: "0",
      highPrice: "14999",
      offerCount: "4",
    },
  };

  return (
    <Fragment>
      <SEO
        title={`${configData.title} India | AXOIX`}
        description={configData.subtitle}
        path={`/industry/${slug}`}
        schema={serviceSchema}
      />
      <style>{pageStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${serviceBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            {/* =========================================================================
                HERO SECTION — High-Impact Domain Presentation
               ========================================================================= */}
            <section className="pg-hero-section">
              <div className="container text-center">
                <div className="pg-badge-pill">
                  <i className={`fas ${config.heroIcon || "fa-layer-group"}`}></i>
                  <span>All-in-One Business Operating System • GST Ready • Voice AI</span>
                </div>

                <h1 className="pg-hero-title">
                  {configData.title}
                </h1>

                <p className="pg-hero-desc">
                  {configData.subtitle}
                </p>

                <div className="pg-hero-btn-group">
                  <Link
                    to={`/signup?vertical=${slug}&plan=explorer&from=${slug}`}
                    className="pg-cta-btn pg-cta-btn-primary"
                  >
                    Get Started Free
                  </Link>
                  {hasDemos && (
                    <Link
                      to={`/demo/${slug}`}
                      className="pg-cta-btn pg-cta-btn-demo"
                    >
                      <i className="fas fa-play-circle me-1"></i>
                      Try Live Demo
                    </Link>
                  )}
                  <a
                    href="#pricing-plans"
                    className="pg-cta-btn pg-cta-btn-plans"
                  >
                    View Pricing Plans
                  </a>
                </div>

                <div className="pg-hero-trust-bullets">
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Built for Indian Operations</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> GST & E-Invoicing Compliant</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Voice Control in 81 Languages</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Free Forever Starter Tier</span>
                </div>

                {/* Sibling solution callout (e.g. PG inside Hotel) */}
                {callout && (
                  <div className="mt-40 text-start" style={{ maxWidth: "860px", margin: "40px auto 0" }}>
                    <div style={{
                      background: "rgba(10, 22, 45, 0.75)",
                      border: "1px solid rgba(0,255,151,0.35)",
                      borderLeft: "4px solid var(--color-primary)",
                      borderRadius: "14px",
                      padding: "24px 28px",
                      backdropFilter: "blur(12px)",
                    }}>
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div>
                          <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "var(--color-primary)", letterSpacing: "0.5px" }}>
                            {callout.eyebrow}
                          </span>
                          <h4 style={{ color: "#fff", margin: "4px 0 6px", fontSize: "1.15rem" }}>{callout.title}</h4>
                          <p style={{ margin: 0, fontSize: "13px", opacity: 0.8, maxWidth: "560px" }}>{callout.body}</p>
                        </div>
                        <div className="d-flex gap-2">
                          <Link to={callout.path} className="thm-btn agency-btn" style={{ padding: "8px 18px", fontSize: "13px" }}>
                            <span className="text">{callout.cta}</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* =========================================================================
                    AUTHENTIC iBOS LIVE PREVIEW WINDOW
                   ========================================================================= */}
                <div className="pg-preview-window">
                  {/* Window Titlebar */}
                  <div className="pg-preview-titlebar">
                    <div className="pg-preview-dots">
                      <span className="pg-preview-dot" style={{ background: "#ef4444" }}></span>
                      <span className="pg-preview-dot" style={{ background: "#f59e0b" }}></span>
                      <span className="pg-preview-dot" style={{ background: "#10b981" }}></span>
                    </div>
                    <div className="pg-preview-urlbar">
                      <i className="fas fa-lock" style={{ fontSize: 9, color: "var(--color-primary)" }}></i>
                      <span>https://{slug}-demo.axoix.io/dashboard</span>
                    </div>
                    <div className="pg-preview-workspace-badge">
                      <i className="fas fa-circle-check me-1"></i> Live iBOS System
                    </div>
                  </div>

                  {/* Header */}
                  <div className="pg-ibos-header">
                    <div>
                      <div className="pg-ibos-header-title">
                        {configData.title} Control Center
                      </div>
                      <div className="pg-ibos-header-sub">
                        Active Workspace · Connected to MySQL Isolated Tenant DB
                      </div>
                    </div>
                    <div className="pg-ibos-header-actions">
                      <select className="pg-ibos-prop-select" defaultValue="0">
                        {(configData.headerBranchOptions || ["Main Branch (HQ)", "Branch 2 (North)"]).map((branch, bIdx) => (
                          <option key={bIdx} value={String(bIdx)}>{branch}</option>
                        ))}
                      </select>
                      <span className="pg-ibos-action-btn">
                        <i className="fas fa-file-export"></i> Export Report
                      </span>
                      <span className="pg-ibos-action-btn pg-ibos-action-btn-primary">
                        <i className="fas fa-plus"></i> {configData.headerQuickAction || "Quick Action"}
                      </span>
                    </div>
                  </div>

                  {/* 8-KPI Stat Grid */}
                  <div className="pg-ibos-stats-grid">
                    {configData.kpis.map((kpi, idx) => (
                      <div key={idx} className="pg-ibos-stat-card">
                        <div className="pg-ibos-stat-label">
                          <span>{kpi.label}</span>
                        </div>
                        <div className="pg-ibos-stat-val">{kpi.value}</div>
                        <div className="pg-ibos-stat-sub text-success">{kpi.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tabs Bar */}
                  <div className="pg-ibos-tabs-bar">
                    {configData.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`pg-ibos-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        <i className={`fas ${tab.icon}`}></i>
                        <span>{tab.label}</span>
                        {tab.badge && (
                          <span className={`pg-ibos-tab-badge ${tab.badgeVariant || "green"}`}>
                            {tab.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Tab Body */}
                  {activeTabObj && (
                    <div style={{ padding: "20px 22px 24px", background: "#0b1222" }}>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 text-start">
                        <div style={{ fontSize: "12px", opacity: 0.85 }}>
                          <i className="fas fa-circle-info me-1.5" style={{ color: "var(--color-primary)" }}></i>
                          {activeTabObj.summaryText}
                        </div>
                        {activeTabObj.actionButton && (
                          <span className="pg-ibos-action-btn pg-ibos-action-btn-primary" style={{ fontSize: "11px", padding: "4px 10px" }}>
                            {activeTabObj.actionButton}
                          </span>
                        )}
                      </div>

                      {/* 1. RESTAURANT — Visual Table Grid */}
                      {slug === "restaurant" && activeTab === "tables" && (
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 text-start" style={{ fontSize: "11px" }}>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }}></span> Available (6)</span>
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", display: "inline-block" }}></span> Occupied (14)</span>
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }}></span> Reserved (2)</span>
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }}></span> Billed / Settle (2)</span>
                            </div>
                            <span style={{ color: "var(--color-primary)", fontWeight: 600 }}>Ground Floor Dining Hall · AC</span>
                          </div>
                          <div className="row g-2 text-center">
                            {[
                              { id: "T-01", seats: "4 Seats", status: "occupied", bill: "₹1,850", time: "28 min" },
                              { id: "T-02", seats: "2 Seats", status: "available", bill: "Ready", time: "Empty" },
                              { id: "T-03", seats: "6 Seats", status: "occupied", bill: "₹3,420", time: "42 min" },
                              { id: "T-04", seats: "4 Seats", status: "billed", bill: "₹2,100", time: "Waiting Pay" },
                              { id: "T-05", seats: "2 Seats", status: "available", bill: "Ready", time: "Empty" },
                              { id: "T-06", seats: "8 Seats", status: "reserved", bill: "VIP", time: "8:30 PM" },
                              { id: "T-07", seats: "4 Seats", status: "occupied", bill: "₹1,240", time: "12 min" },
                              { id: "T-08", seats: "2 Seats", status: "available", bill: "Ready", time: "Empty" },
                            ].map((tbl) => (
                              <div key={tbl.id} className="col-6 col-sm-4 col-md-3">
                                <div className={`ibos-table-tile ibos-table-${tbl.status}`}>
                                  <span className="fw-bold fs-5 mb-1">{tbl.id}</span>
                                  <span style={{ fontSize: "11px", opacity: 0.85 }}>{tbl.seats}</span>
                                  <span className="fw-bold mt-1" style={{ fontSize: "12px", color: tbl.status === "available" ? "#34d399" : tbl.status === "billed" ? "#fbbf24" : "#fca5a5" }}>
                                    {tbl.bill}
                                  </span>
                                  <span style={{ fontSize: "10px", opacity: 0.65 }}>{tbl.time}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 2. RESTAURANT — Live Kitchen Display (KDS) */}
                      {slug === "restaurant" && activeTab === "kds" && (
                        <div className="row g-3 text-start">
                          {[
                            { id: "KOT-921", table: "Table 04 (4 Covers)", type: "Dine-In", time: "08:12 min", urgency: "normal", items: ["2x Butter Chicken (Curry)", "4x Garlic Naan (Tandoor)", "1x Dal Makhani"], note: "Medium spicy, less butter", action: "Mark Ready ✓" },
                            { id: "KOT-922", table: "Table 12 (6 Covers)", type: "Dine-In", time: "14:30 min", urgency: "warning", items: ["1x Paneer Tikka Platter", "2x Butter Naan", "1x Veg Biryani"], note: "Serve sizzler style hot", action: "Mark Ready ✓" },
                            { id: "KOT-924", table: "Zomato #Z-492", type: "Dispatch", time: "22:10 min", urgency: "urgent", items: ["1x Royal Biryani Box", "2x Gulab Jamun", "1x Raita"], note: "Sealed packaging required", action: "Ready for Pickup ✓" },
                          ].map((kot) => (
                            <div key={kot.id} className="col-lg-4 col-md-6">
                              <div className={`ibos-kds-card ibos-kds-${kot.urgency}`}>
                                <div className="p-3" style={{ background: kot.urgency === "urgent" ? "#7f1d1d" : kot.urgency === "warning" ? "#78350f" : "#064e3b" }}>
                                  <div className="d-flex justify-content-between align-items-center text-white">
                                    <strong className="fs-6">{kot.id}</strong>
                                    <span style={{ fontSize: "11px" }}><i className="fas fa-clock me-1"></i>{kot.time}</span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center mt-1 text-white-75" style={{ fontSize: "11px" }}>
                                    <span>{kot.table}</span>
                                    <span className="badge bg-dark text-white">{kot.type}</span>
                                  </div>
                                </div>
                                <div className="p-3">
                                  <ul className="list-unstyled mb-2" style={{ fontSize: "12px", lineHeight: 1.8 }}>
                                    {kot.items.map((it, iIdx) => (
                                      <li key={iIdx}>• {it}</li>
                                    ))}
                                  </ul>
                                  {kot.note && (
                                    <div className="p-1 px-2 rounded mb-3" style={{ background: "rgba(255,255,255,0.05)", fontSize: "11px", color: "#fde047" }}>
                                      <i className="fas fa-info-circle me-1"></i>Note: {kot.note}
                                    </div>
                                  )}
                                  <button type="button" className="btn btn-sm w-100 fw-bold" style={{ background: "var(--color-primary)", color: "#0b1226", fontSize: "11px", borderRadius: "6px" }}>
                                    {kot.action}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 3. HOTEL — Room Rack Status Board */}
                      {slug === "hotel" && activeTab === "matrix" && (
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 text-start" style={{ fontSize: "11px" }}>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }}></span> Vacant Clean (8)</span>
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }}></span> Occupied (48)</span>
                              <span className="d-inline-flex align-items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }}></span> Needs Housekeeping (4)</span>
                            </div>
                            <span style={{ color: "var(--color-primary)", fontWeight: 600 }}>60 Total Rooms · 80% Occupancy</span>
                          </div>
                          <div className="mb-3 text-start">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className="text-uppercase fw-bold text-white-50" style={{ fontSize: "11px" }}>Floor 1 · Deluxe King Rooms</span>
                              <div className="flex-grow-1" style={{ height: "1px", background: "rgba(255,255,255,0.1)" }}></div>
                            </div>
                            <div className="row g-2">
                              {[
                                { num: "101", cat: "Deluxe", guest: "R. Malhotra", dot: "#3b82f6" },
                                { num: "102", cat: "Deluxe", guest: "Available", dot: "#10b981" },
                                { num: "103", cat: "Deluxe", guest: "A. Verma", dot: "#3b82f6" },
                                { num: "104", cat: "Deluxe", guest: "Housekeeping", dot: "#f59e0b" },
                                { num: "105", cat: "Deluxe", guest: "Available", dot: "#10b981" },
                                { num: "106", cat: "Deluxe", guest: "K. Johar", dot: "#3b82f6" },
                              ].map((rm) => (
                                <div key={rm.num} className="col-4 col-sm-3 col-md-2">
                                  <div className="ibos-room-tile">
                                    <span style={{ position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: "50%", background: rm.dot }}></span>
                                    <strong className="fs-6 d-block">{rm.num}</strong>
                                    <span style={{ fontSize: "10px", opacity: 0.7 }}>{rm.cat}</span>
                                    <span className="d-block mt-1 text-truncate" style={{ fontSize: "11px", color: rm.dot }}>{rm.guest}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 4. GYM & FITNESS — Biometric Turnstiles */}
                      {slug === "gym-fitness" && activeTab === "turnstile" && (
                        <div className="text-start">
                          {[
                            { name: "Rohan Verma", plan: "Annual Platinum Member", time: "07:14:02 AM", gate: "Turnstile Gate 1", trainer: "Trainer: Vikram R.", status: "Access Granted ✓", color: "#10b981" },
                            { name: "Sneha Kulkarni", plan: "3-Month CrossFit Batch", time: "07:11:18 AM", gate: "Turnstile Gate 1", trainer: "Trainer: Anjali S.", status: "Access Granted ✓", color: "#10b981" },
                            { name: "Amit Joshi", plan: "Monthly Strength", time: "07:05:40 AM", gate: "Turnstile Gate 2", trainer: "Self Workout", status: "Overdue Alert (2 Days) ⚠", color: "#f59e0b" },
                            { name: "Kavita Roy", plan: "Annual Gold Member", time: "06:58:12 AM", gate: "Turnstile Gate 1", trainer: "Trainer: Vikram R.", status: "Access Granted ✓", color: "#10b981" },
                          ].map((chk, cIdx) => (
                            <div key={cIdx} className="ibos-turnstile-row">
                              <div className="d-flex align-items-center gap-3">
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,255,151,0.15)", color: "var(--color-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                                  {chk.name.charAt(0)}
                                </div>
                                <div>
                                  <strong className="text-white d-block" style={{ fontSize: "13px" }}>{chk.name}</strong>
                                  <span style={{ fontSize: "11px", opacity: 0.7 }}>{chk.plan} · {chk.trainer}</span>
                                </div>
                              </div>
                              <div className="text-end">
                                <span className="badge" style={{ background: `${chk.color}22`, color: chk.color, fontSize: "11px" }}>{chk.status}</span>
                                <span className="d-block text-white-50 mt-1" style={{ fontSize: "10px" }}>{chk.gate} · {chk.time}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 5. HEALTHCARE — OPD Token Queue */}
                      {slug === "healthcare" && activeTab === "opd" && (
                        <div className="row g-3 text-start">
                          <div className="col-lg-6">
                            <div className="ibos-opd-card">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-success text-white">Active in Chamber</span>
                                <span style={{ color: "var(--color-primary)", fontSize: "12px", fontWeight: 700 }}>Room 102</span>
                              </div>
                              <h4 className="text-white mb-1">Token #18 — Ramesh Gupta</h4>
                              <span style={{ fontSize: "12px", color: "#94a3b8" }}>52 Yrs / Male · Dr. Rajesh Sharma (Cardiology)</span>
                              <div className="d-flex gap-2 flex-wrap my-3 p-2 rounded" style={{ background: "rgba(255,255,255,0.05)", fontSize: "11px" }}>
                                <span>BP: <strong>128/84</strong></span>
                                <span>• Pulse: <strong>72 bpm</strong></span>
                                <span>• SpO2: <strong>99%</strong></span>
                                <span>• Temp: <strong>98.4°F</strong></span>
                              </div>
                              <div className="d-flex gap-2">
                                <button type="button" className="btn btn-sm btn-primary px-3 fw-bold" style={{ fontSize: "11px" }}>Call Next (Token #19) →</button>
                                <button type="button" className="btn btn-sm btn-outline-light px-3" style={{ fontSize: "11px" }}>Write E-Prescription</button>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div style={{ background: "#111a30", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px 20px" }}>
                              <span className="text-uppercase fw-bold text-white-50 mb-2 d-block" style={{ fontSize: "11px" }}>Upcoming Queue in Waiting Lounge</span>
                              {[
                                { token: "#19", name: "Sangeeta Rao (38/F)", wait: "Est. wait 8 min" },
                                { token: "#20", name: "Mohammad Farhan (29/M)", wait: "Est. wait 18 min" },
                                { token: "#21", name: "Sunita Devi (64/F)", wait: "Est. wait 28 min" },
                              ].map((q, qIdx) => (
                                <div key={qIdx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-white-10" style={{ fontSize: "12px" }}>
                                  <div>
                                    <strong style={{ color: "#38bdf8" }}>{q.token}</strong> — <span className="text-white">{q.name}</span>
                                  </div>
                                  <div className="text-end">
                                    <span style={{ color: "#a5f3fc", fontSize: "11px" }}>{q.wait}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 6. EDUCATION — Fees & Gate Attendance Split Dashboard */}
                      {slug === "education" && (activeTab === "fees" || activeTab === "attendance") && (
                        <div className="row g-3 text-start">
                          <div className="col-lg-6">
                            <div style={{ background: "#111a30", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px 20px" }}>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-uppercase fw-bold text-white-50" style={{ fontSize: "11px" }}>Recent Fee Collections & Defaulters</span>
                                <span className="badge bg-danger text-white" style={{ fontSize: "10px" }}>14 Defaulters</span>
                              </div>
                              {[
                                { name: "Aryan Malhotra", class: "Class 10-A", amount: "₹18,500", status: "Paid via UPI ✓", variant: "paid", action: "Receipt Slip" },
                                { name: "Kavita Roy", class: "Class 9-B · Overdue 18 Days", amount: "₹12,000", status: "Overdue", variant: "due", action: "Send WhatsApp UPI" },
                                { name: "Rohan Verma", class: "Class 11-Science", amount: "₹22,000", status: "Paid via NetBanking ✓", variant: "paid", action: "Receipt Slip" },
                                { name: "Tanvi Patel", class: "Class 8-C · Overdue 24 Days", amount: "₹8,500", status: "Overdue", variant: "due", action: "Send WhatsApp UPI" },
                              ].map((f, fIdx) => (
                                <div key={fIdx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-white-10" style={{ fontSize: "12px" }}>
                                  <div>
                                    <div className="text-white fw-semibold">{f.name}</div>
                                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>{f.class}</div>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-bold text-white">{f.amount}</div>
                                    <span className={`pg-ibos-badge-${f.variant}`} style={{ fontSize: "10px", padding: "2px 6px" }}>{f.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div style={{ background: "#111a30", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px 20px" }}>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-uppercase fw-bold text-white-50" style={{ fontSize: "11px" }}>Live RFID Gate Attendance (Turnstiles)</span>
                                <span className="badge bg-success text-white" style={{ fontSize: "10px" }}>96.4% Present</span>
                              </div>
                              {[
                                { rfid: "RFID-091", name: "Rohan Verma", meta: "Class 11-Sci · Gate 01 · 07:48 AM", status: "Present ✓", variant: "paid" },
                                { rfid: "RFID-092", name: "Ananya Sen", meta: "Class 10-A · Gate 02 · 07:52 AM", status: "Present ✓", variant: "paid" },
                                { rfid: "RFID-093", name: "Aditya Nair", meta: "Class 8-B · Unexcused Absence", status: "Absent • SMS Sent", variant: "due" },
                                { rfid: "RFID-094", name: "Sneha Iyer", meta: "Class 9-A · Gate 01 · 07:55 AM", status: "Present ✓", variant: "paid" },
                              ].map((att, aIdx) => (
                                <div key={aIdx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-white-10" style={{ fontSize: "12px" }}>
                                  <div>
                                    <span style={{ color: "#38bdf8", fontFamily: "monospace", fontSize: "11px", marginRight: "6px" }}>{att.rfid}</span>
                                    <strong className="text-white">{att.name}</strong>
                                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>{att.meta}</div>
                                  </div>
                                  <div>
                                    <span className={`pg-ibos-badge-${att.variant}`} style={{ fontSize: "10px" }}>{att.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 7. SALON & SPA — Chair Schedule & Stylist Roster */}
                      {slug === "salon-spa" && (activeTab === "appointments" || activeTab === "stylists") && (
                        <div className="row g-3 text-start">
                          <div className="col-lg-6">
                            <div style={{ background: "#111a30", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px 20px" }}>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-uppercase fw-bold text-white-50" style={{ fontSize: "11px" }}>Live Chair Appointments</span>
                                <span className="badge bg-primary text-white" style={{ fontSize: "10px" }}>4 Active Chairs</span>
                              </div>
                              {[
                                { chair: "Chair 02", client: "Priya Mehra", service: "Hair Spa + Hydra Facial", stylist: "Sameer K.", fee: "₹3,200", status: "In-Chair 💇", variant: "paid" },
                                { chair: "Chair 04", client: "Rhea Kapoor", service: "Keratin Hair Smoothing", stylist: "Pooja M.", fee: "₹5,500", status: "Waiting in Lounge", variant: "notice" },
                                { chair: "Chair 01", client: "Ananya Sen", service: "Gel Manicure & Pedicure", stylist: "Neha S.", fee: "₹1,800", status: "Completed ✓", variant: "paid" },
                                { chair: "Chair 03", client: "Dr. Sunita Rao", service: "Bridal Makeup Trial", stylist: "Sameer K.", fee: "₹4,500", status: "Scheduled 02:00 PM", variant: "paid" },
                              ].map((apt, aptIdx) => (
                                <div key={aptIdx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-white-10" style={{ fontSize: "12px" }}>
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>{apt.chair}</span>
                                      <strong className="text-white">{apt.client}</strong>
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>{apt.service} • {apt.stylist}</div>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-bold text-white">{apt.fee}</div>
                                    <span className={`pg-ibos-badge-${apt.variant}`} style={{ fontSize: "10px" }}>{apt.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div style={{ background: "#111a30", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px 20px" }}>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-uppercase fw-bold text-white-50" style={{ fontSize: "11px" }}>Stylist Availability & Shift Commissions</span>
                                <span className="badge bg-success text-white" style={{ fontSize: "10px" }}>6 Active Stylists</span>
                              </div>
                              {[
                                { name: "Sameer Khan (Senior Stylist)", chair: "Chair 02", clients: "5 Clients", commission: "₹1,600 (20%)", status: "In-Service 💇", variant: "paid" },
                                { name: "Pooja Mishra (Color Specialist)", chair: "Chair 04", clients: "4 Clients", commission: "₹1,450 (20%)", status: "Waiting", variant: "notice" },
                                { name: "Neha Sharma (Nail & Skin)", chair: "Chair 01", clients: "6 Clients", commission: "₹1,200 (15%)", status: "Available", variant: "paid" },
                                { name: "Vikram Joshi (Master Barber)", chair: "Chair 05", clients: "7 Clients", commission: "₹1,800 (20%)", status: "In-Service ✂️", variant: "paid" },
                              ].map((sty, sIdx) => (
                                <div key={sIdx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-white-10" style={{ fontSize: "12px" }}>
                                  <div>
                                    <div className="text-white fw-semibold">{sty.name}</div>
                                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>{sty.chair} • {sty.clients}</div>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-bold" style={{ color: "var(--color-primary)" }}>{sty.commission}</div>
                                    <span className={`pg-ibos-badge-${sty.variant}`} style={{ fontSize: "10px" }}>{sty.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* DEFAULT: Renders standard operational records with clean iBOS table */}
                      {!(
                        (slug === "restaurant" && (activeTab === "tables" || activeTab === "kds")) ||
                        (slug === "hotel" && activeTab === "matrix") ||
                        (slug === "gym-fitness" && activeTab === "turnstile") ||
                        (slug === "healthcare" && activeTab === "opd") ||
                        (slug === "education" && (activeTab === "fees" || activeTab === "attendance")) ||
                        (slug === "salon-spa" && (activeTab === "appointments" || activeTab === "stylists"))
                      ) && (
                        <div className="table-responsive">
                          <table className="table table-dark table-sm mb-0 text-start" style={{ fontSize: "12px", background: "transparent" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", opacity: 0.8 }}>
                                {activeTabObj.tableHeaders.map((head, hIdx) => (
                                  <th key={hIdx}>{head}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {activeTabObj.rows.map((row, rIdx) => (
                                <tr key={rIdx} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                  <td><strong style={{ color: "#38bdf8", fontFamily: "monospace" }}>{row.col1}</strong></td>
                                  <td>{row.col2}</td>
                                  <td>{row.col3}</td>
                                  <td>{row.col4}</td>
                                  <td>{row.col5}</td>
                                  {row.badge && (
                                    <td>
                                      <span className={`pg-ibos-badge-${row.badge.variant || "paid"}`}>
                                        {row.badge.text}
                                      </span>
                                    </td>
                                  )}
                                  {row.action && (
                                    <td>
                                      <span style={{ color: "var(--color-primary)", cursor: "default", fontWeight: 600 }}>
                                        {row.action}
                                      </span>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </section>

            {/* =========================================================================
                OPERATIONAL TRUST BAR
               ========================================================================= */}
            <section className="pg-trust-bar">
              <div className="container">
                <div className="row g-3">
                  {configData.trustBar.map((item, idx) => (
                    <div key={idx} className="col-lg-3 col-md-6">
                      <div className="pg-trust-item">
                        <div className="pg-trust-icon"><i className={`fas ${item.icon}`}></i></div>
                        <div>
                          <div className="pg-trust-title">{item.title}</div>
                          <p className="pg-trust-desc">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                3-STEP FAST SETUP — Live in 3 Minutes
               ========================================================================= */}
            <section className="pt-80 pb-60">
              <div className="container">
                <div className="text-center mb-50">
                  <span className="sub-title mb-15">Zero Technical Setup Required</span>
                  <h2 className="title">Set Up Your Entire {title} in 3 Simple Steps</h2>
                  <p className="opacity-75 mt-15" style={{ maxWidth: 680, margin: "15px auto 0" }}>
                    No complex software configuration. Enter your property or business details and start running operations today.
                  </p>
                </div>

                <div className="row g-4">
                  {configData.setupSteps.map((step, idx) => (
                    <div key={idx} className="col-lg-4 col-md-6">
                      <div className="pg-step-card">
                        <span className="pg-step-num">{step.step}</span>
                        <h3 style={{ fontSize: "1.2rem", marginBottom: "12px", color: "#fff" }}>
                          {step.title}
                        </h3>
                        <p className="opacity-75 mb-0" style={{ fontSize: "14px", lineHeight: 1.7 }}>
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                THE 4 CORE OPERATIONAL PILLARS
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-50">
                  <span className="sub-title mb-15">Operational Excellence</span>
                  <h2 className="title">The 4 Pillars That Stop Daily Headaches</h2>
                </div>

                <div className="row g-4">
                  {configData.pillars.map((pillar, idx) => (
                    <div key={idx} className="col-lg-6">
                      <div className="pg-pillar-card">
                        <span className="pg-pillar-badge">{pillar.badge}</span>
                        <h3 style={{ fontSize: "1.35rem", marginBottom: "14px", color: "#fff" }}>
                          <i className={`fas ${pillar.icon} me-2`} style={{ color: "var(--color-primary)" }}></i>
                          {pillar.title}
                        </h3>
                        <p className="opacity-80 mb-20" style={{ lineHeight: 1.7 }}>
                          {pillar.desc}
                        </p>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "14px" }}>
                          {pillar.bullets.map((b, bIdx) => (
                            <li key={bIdx} className="mb-2">
                              <i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i>
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                HEAD-TO-HEAD COMPARISON TABLE
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <span className="sub-title mb-15">Clear Honest Comparison</span>
                  <h2 className="title">Why Businesses Choose AXOIX</h2>
                  <p className="opacity-75 mt-15" style={{ maxWidth: 700, margin: "15px auto 0" }}>
                    Compare AXOIX against traditional registers, disconnected spreadsheets, and expensive single-purpose point apps.
                  </p>
                </div>

                <div className="table-responsive" style={{ maxWidth: 940, margin: "0 auto" }}>
                  <table className="pg-compare-table">
                    <thead>
                      <tr>
                        <th>Operational Capability</th>
                        <th style={{ color: "var(--color-primary)" }}>AXOIX {title}</th>
                        <th>{configData.competitorName || "Single-Purpose Apps"}</th>
                        <th>Excel / Paper Pad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configData.comparisonRows.map((row, idx) => (
                        <tr key={idx}>
                          <td><strong>{row.capability}</strong></td>
                          <td style={{ color: "var(--color-primary)", fontWeight: 600 }}>{row.axoix}</td>
                          <td>{row.competitor}</td>
                          <td>{row.traditional}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {comparisonLinks && comparisonLinks.length > 0 && (
                  <div className="text-center mt-30">
                    <Link
                      to={`/compare/${comparisonLinks[0].slug}`}
                      className="thm-btn agency-btn"
                      style={{ background: "transparent", border: "1px solid rgba(0,255,151,0.3)" }}
                    >
                      <span className="text">Read the Full AXOIX vs {comparisonLinks[0].name} Breakdown →</span>
                    </Link>
                  </div>
                )}
              </div>
            </section>

            {/* =========================================================================
                VERTICAL PRICING PLANS — Unified Theme Pricing Section
               ========================================================================= */}
            <PricingSection
              hideBg
              hideComparison
              hideAddons
              hideFaqHelp
              sectionId="pricing-plans"
              className="pricing pt-60 pb-80"
              verticalSlug={slug}
              customSubtitle="Simple Transparent Pricing"
              customTitle={`Choose the Right Plan for Your ${title}`}
              customDesc={`Every plan runs the full ${title} engine on an isolated MySQL tenant database. You only pay for your capacity as you scale.`}
            />


            {/* =========================================================================
                KEY FEATURES GRID
               ========================================================================= */}
            {features.length > 0 && (
              <section className="pt-60 pb-60">
                <div className="container">
                  <div className="text-center mb-50">
                    <span className="sub-title mb-15">{t('industryDetail.featuresSubtitle', { defaultValue: 'Key Features' })}</span>
                    <h2 className="title">{t('industryDetail.featuresTitle', { defaultValue: 'What You Get with {{vertical}}', vertical: title })}</h2>
                  </div>
                  <div className="row">
                    {features.map((feature, idx) => (
                      <div key={idx} className="col-lg-4 col-md-6 mb-20">
                        <div className="industry-detail-feature-card">
                          <div className="industry-detail-feature-icon">
                            <i className="fas fa-check"></i>
                          </div>
                          <p className="mb-0 opacity-85" style={{ lineHeight: 1.7 }}>{feature}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                PUBLIC STOREFRONT & WEBSITE BUILDER
               ========================================================================= */}
            {config.hasStorefront && (
              <section className="pb-60">
                <div className="container">
                  <div className="industry-detail-feature-card p-40">
                    <div className="row align-items-center">
                      <div className="col-lg-7">
                        <h3 className="mb-15" style={{ fontSize: "1.45rem" }}>
                          <i className="fas fa-globe me-2" style={{ color: "var(--color-primary)" }}></i>
                          Your Own Branded {title} Website Included
                        </h3>
                        <p className="opacity-75 mb-20" style={{ lineHeight: 1.7 }}>
                          Every subscription includes a ready-to-publish customer website with online booking or ordering. Customers can browse services, place requests, and make payments online 24/7 with zero commission cuts.
                        </p>
                        <div className="d-flex gap-2 flex-wrap">
                          {["Custom Domain Support", "SEO Optimized", "Mobile Responsive", "Instant UPI QR Payments"].map((tag, idx) => (
                            <span key={idx} className="industry-detail-capability-tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                      <div className="col-lg-5 text-center mt-30 mt-lg-0">
                        <div style={{ fontSize: "72px", color: "var(--color-primary)", opacity: 0.35 }}>
                          <i className="fas fa-store"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                BUILT-IN BUSINESS BACK-OFFICE SUITE
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <h2 className="title" style={{ fontSize: "1.75rem" }}>Complete Business Operating System Included</h2>
                  <p className="opacity-75 mt-10" style={{ maxWidth: 650, margin: "10px auto 0" }}>
                    Run daily operations without buying separate software for accounting, GST filings, or staff payroll.
                  </p>
                </div>
                <div className="industry-detail-module-grid">
                  {CORE_MODULES.map((mod, idx) => (
                    <div key={idx} className="industry-detail-module-item">
                      <i className={`fas ${mod.icon}`}></i>
                      <span>{t(mod.nameKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                PLATFORM SPECIFICATIONS & TRANSPARENCY
               ========================================================================= */}
            <section className="pt-20 pb-40">
              <div className="container">
                <div
                  className="pricing-item xb-border bg_img p-40"
                  style={{
                    backgroundImage: `url(${cardBg})`,
                    backgroundSize: "cover",
                    backgroundColor: "#0b1222",
                  }}
                >
                  <div className="d-flex align-items-center mb-25" style={{ gap: "16px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(0, 255, 151, 0.12)",
                        border: "1px solid rgba(0, 255, 151, 0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--color-primary)",
                        fontSize: "20px",
                        flexShrink: 0,
                      }}
                    >
                      <i className="fas fa-shield-halved"></i>
                    </div>
                    <div>
                      <h3 className="mb-1 text-white" style={{ fontSize: "1.35rem", fontWeight: 700 }}>
                        Platform Transparency & Specifications
                      </h3>
                      <p className="mb-0 text-white-50" style={{ fontSize: "0.95rem" }}>
                        Clear architecture standards, uptime guarantees, and transparent operational limits.
                      </p>
                    </div>
                  </div>
                  <div className="row g-3">
                    {configData.transparencyPoints.map((point, idx) => (
                      <div key={idx} className="col-md-6 d-flex align-items-start" style={{ gap: "12px" }}>
                        <i
                          className="fas fa-circle-check mt-1"
                          style={{ color: "var(--color-primary)", fontSize: "16px", flexShrink: 0 }}
                        ></i>
                        <span style={{ fontSize: "0.92rem", color: "#e2e8f0", lineHeight: 1.55 }}>
                          {point}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* =========================================================================
                OPERATOR FAQ — Theme Native Accordion
               ========================================================================= */}
            {configData.faqList.length > 0 && (
              <section className="pt-40 pb-60">
                <div className="container">
                  <div className="sec-title sec-title--two pricing-sec-title text-center mb-50">
                    <span className="sub-title mb-25 wow fadeInUp" data-wow-duration="600ms">
                      <img src={subLeftIcon} alt="icon" /> FAQ’s <img src={subRightIcon} alt="icon" />
                    </span>
                    <h2 className="title wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
                      Got Questions About AXOIX {title}?
                    </h2>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-lg-12">
                      <div className="xb-faq wow fadeInUp" data-wow-duration="600ms">
                        <ul className="accordion_box clearfix list-unstyled">
                          {configData.faqList.map((faq, idx) => {
                            const isOpen = activeFaqId === idx;
                            const numStr = idx + 1 < 10 ? `0${idx + 1}` : `${idx + 1}`;
                            return (
                              <li
                                key={idx}
                                className={`accordion block ${isOpen ? "active-block" : ""}`}
                              >
                                <div
                                  className={`acc-btn ${isOpen ? "active" : ""}`}
                                  onClick={() => setActiveFaqId(isOpen ? null : idx)}
                                >
                                  <span className="number">{numStr}</span>
                                  _ {faq.question}
                                  <span className="arrow">
                                    <span></span>
                                  </span>
                                </div>
                                <div
                                  className={`acc_body ${isOpen ? "current" : ""}`}
                                  style={{
                                    maxHeight: isOpen ? "500px" : "0px",
                                    overflow: "hidden",
                                    transition: "max-height 0.4s ease",
                                  }}
                                >
                                  <div className="content">
                                    <p>{faq.answer}</p>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                BOTTOM HIGH-URGENCY CTA SECTION
               ========================================================================= */}
            <section className="pt-60 pb-100">
              <div className="container">
                <div className="text-center pricing-item xb-border bg_img p-50 industry-detail-cta-section">
                  <div className="pg-badge-pill mb-20">
                    <i className="fas fa-lock-open"></i>
                    <span>Free Forever Tier • No Credit Card Required</span>
                  </div>
                  <h2 className="mb-15" style={{ fontSize: "2.2rem" }}>
                    Ready to Transform Your {title} Business?
                  </h2>
                  <p className="opacity-75 mb-30" style={{ maxWidth: "620px", margin: "0 auto 30px", fontSize: "1.05rem", lineHeight: 1.7 }}>
                    Sign up for the free Explorer plan today. Enjoy isolated database security, GST invoicing, and team chat. Upgrade anytime as your transaction volume expands.
                  </p>
                  <div className="pg-hero-btn-group justify-content-center">
                    <Link
                      to={`/signup?vertical=${slug}&from=${slug}`}
                      className="pg-cta-btn pg-cta-btn-primary"
                    >
                      Get Started Free
                    </Link>
                    {hasDemos && (
                      <Link
                        to={`/demo/${slug}`}
                        className="pg-cta-btn pg-cta-btn-demo"
                      >
                        <i className="fas fa-play-circle me-1"></i>
                        Try Live Demo
                      </Link>
                    )}
                    <Link
                      to="/pricing"
                      className="pg-cta-btn pg-cta-btn-plans"
                    >
                      View Pricing
                    </Link>
                  </div>
                  <p className="opacity-60 mt-25 mb-0" style={{ fontSize: "13px", maxWidth: "640px", margin: "25px auto 0" }}>
                    Instant cloud setup in 3 minutes • 100% data residency in India • Full data export anytime
                  </p>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default IndustryDetailsPage;
